import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";

import { i18next } from "../i18n";
import { ChevronLeft, ChevronRight, Clock, X } from "../widgets/icons";
import { Message } from "./message";
import type { Model } from "./model";
import { asOfOf } from "./routes";
import { TIME_TRAVEL_ROOT_ID } from "./update";

// ponytail: weeks start Monday everywhere; Intl.Locale#getWeekInfo is missing in Firefox.
// 2024-01-01 was a Monday, so these are the user's own short weekday names, Monday first.
const WEEKDAYS = Array.from({ length: 7 }, (_, i) =>
	new Date(2024, 0, 1 + i).toLocaleDateString(undefined, { weekday: "short" }),
);

/** `2026-01-01` (the picked calendar day) in the user's own date format. */
export const formatPickedDate = (date: string): string => {
	const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
	return new Date(year, month - 1, day).toLocaleDateString(undefined, {
		dateStyle: "medium",
	});
};

const pad = (n: number): string => String(n).padStart(2, "0");
const isoDate = (d: Date): string =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoMonth = (d: Date): string =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

const parseMonth = (month: string): [number, number] => {
	const [year = 0, monthNumber = 1] = month.split("-").map(Number);
	return [year, monthNumber];
};

export const shiftMonth = (month: string, delta: number): string => {
	const [year, monthNumber] = parseMonth(month);
	return isoMonth(new Date(year, monthNumber - 1 + delta, 1));
};

const noActivity: { days: Record<string, number> } = { days: {} };

/** v1 `getHeatmapClass`. */
const heat = (count: number): string =>
	count >= 6
		? "bg-primary text-primary-content"
		: count >= 3
			? "bg-primary/50"
			: count >= 1
				? "bg-primary/20"
				: "";

/** At "now" the trigger is a quiet icon like the gear beside it; only time travel earns an amber dated chip. */
const chipClass = (isActive: boolean): string =>
	isActive
		? "flex h-8 items-center gap-1.5 rounded-lg border border-secondary bg-secondary/10 px-2.5 text-xs whitespace-nowrap text-secondary"
		: "btn btn-ghost btn-sm btn-square text-base-content/70";

/**
 * v1's `TimeTravelDatePicker`: a DaisyUI focus dropdown with a month grid and
 * activity heatmap. The trigger is a clock icon at "now", and "Viewing: <date>" in
 * amber while travelling. `asOf` lives in the URL, never in the Model (KTD3);
 * the viewed month and its heatmap live in `model.activityMonth`/`activity`.
 */
export const timeTravelPicker = (
	model: Model,
	h: HtmlBuilder<Message>,
): Html => {
	const asOf = asOfOf(model.route);
	const isActive = Option.isSome(asOf);
	const today = new Date();
	const todayStr = isoDate(today);
	const selected = Option.map(asOf, (value) => value.slice(0, 10));
	const month =
		model.activityMonth !== ""
			? model.activityMonth
			: Option.match(asOf, {
					onNone: () => isoMonth(today),
					onSome: (value) => value.slice(0, 7),
				});
	const [year, monthNumber] = parseMonth(month);
	const startWeekday = (new Date(year, monthNumber - 1, 1).getDay() + 6) % 7;
	const daysInMonth = new Date(year, monthNumber, 0).getDate();
	const monthLabel = new Date(year, monthNumber - 1, 1).toLocaleDateString(
		undefined,
		{ month: "short", year: "numeric" },
	);
	const isCurrentMonth = month === isoMonth(today);
	const counts = AsyncData.getOrElse(model.activity, () => noActivity).days;
	const loading =
		AsyncData.isLoading(model.activity) ||
		AsyncData.isRefreshing(model.activity);

	const dayCell = (day: number): Html => {
		const date = `${month}-${pad(day)}`;
		const isFuture = date > todayStr;
		const isSelected = Option.contains(selected, date);
		const count = counts[date] ?? 0;
		const label = new Date(year, monthNumber - 1, day).toLocaleDateString(
			undefined,
			{ month: "long", day: "numeric" },
		);
		return h.button(
			[
				h.Type("button"),
				h.Role("gridcell"),
				h.AriaLabel(
					count === 0
						? label
						: `${label}, ${i18next.t("timeTravel.events", { count })}`,
				),
				h.AriaSelected(isSelected),
				h.Disabled(isFuture),
				h.Class(
					[
						"w-full aspect-square rounded text-xs flex items-center justify-center transition-colors",
						isFuture
							? "opacity-30 cursor-not-allowed"
							: "cursor-pointer hover:ring-1 hover:ring-primary",
						date === todayStr ? "ring-1 ring-base-content/30" : "",
						isSelected ? "ring-2 ring-secondary" : "",
						heat(count),
					]
						.filter(Boolean)
						.join(" "),
				),
				h.OnClick(Message.SelectedTimeTravelDate({ date })),
			],
			[String(day)],
		);
	};

	const monthButton = (delta: number, label: string, disabled: boolean) =>
		h.button(
			[
				h.Type("button"),
				h.Class("btn btn-ghost btn-xs btn-circle"),
				h.AriaLabel(label),
				h.Disabled(disabled),
				h.OnClick(
					Message.ClickedTimeTravelMonth({ month: shiftMonth(month, delta) }),
				),
			],
			[
				delta < 0
					? ChevronLeft(h, { size: 14 })
					: ChevronRight(h, { size: 14 }),
			],
		);

	return h.div(
		[h.Class("flex items-center gap-1")],
		[
			h.details(
				[
					h.Id(TIME_TRAVEL_ROOT_ID),
					// Lives in the sidebar footer, so it opens upward.
					h.Class("dropdown dropdown-top"),
					h.Open(model.timeTravelOpen),
					h.OnToggle((isOpen) => Message.ToggledTimeTravel({ isOpen, month })),
				],
				[
					h.summary(
						[
							h.Role("button"),
							h.DataAttribute("testid", "time-travel"),
							h.Class(`${chipClass(isActive)} list-none`),
							h.AriaLabel(i18next.t("timeTravel.timeTravel")),
							h.Title(i18next.t("timeTravel.timeTravel")),
						],
						Option.match(selected, {
							onNone: () => [Clock(h, { size: 18 })],
							onSome: (date) => [
								Clock(h, { size: 14 }),
								h.span(
									[h.Class("hidden xl:inline opacity-70")],
									[i18next.t("timeTravel.viewingPrefix")],
								),
								h.span([], [formatPickedDate(date)]),
							],
						}),
					),
					h.div(
						[
							h.Tabindex(0),
							h.Class(
								"dropdown-content z-50 my-2 w-80 max-w-[calc(100vw-2rem)] rounded-box bg-base-100 border border-base-300 p-4 shadow-lg",
							),
						],
						[
							h.div(
								[h.Class("flex items-center justify-between mb-3")],
								[
									h.h3(
										[h.Class("font-semibold text-sm")],
										[i18next.t("timeTravel.timeTravelTitle")],
									),
									isActive
										? h.button(
												[
													h.Type("button"),
													h.Class("btn btn-ghost btn-xs"),
													h.AriaLabel(i18next.t("timeTravel.exitTimeTravel")),
													h.OnClick(Message.ClickedExitTimeTravel()),
												],
												[i18next.t("timeTravel.now")],
											)
										: h.empty,
								],
							),
							h.div(
								[h.Class("flex items-center justify-between mb-2")],
								[
									monthButton(-1, i18next.t("timeTravel.previousMonth"), false),
									h.span(
										[h.Class("text-sm font-medium flex items-center gap-2")],
										[
											monthLabel,
											loading
												? h.span(
														[
															h.Role("status"),
															h.Class("loading loading-spinner loading-xs"),
														],
														[],
													)
												: h.empty,
										],
									),
									monthButton(
										1,
										i18next.t("timeTravel.nextMonth"),
										isCurrentMonth,
									),
								],
							),
							h.div(
								[
									h.Role("grid"),
									h.AriaLabel(i18next.t("timeTravel.activityCalendar")),
								],
								[
									h.div(
										[h.Role("row"), h.Class("grid grid-cols-7 gap-0.5 mb-1")],
										WEEKDAYS.map((day) =>
											h.div(
												[
													h.Role("columnheader"),
													h.Class(
														"text-center text-xs text-base-content/50 font-medium",
													),
												],
												[day],
											),
										),
									),
									h.div(
										[h.Class("grid grid-cols-7 gap-0.5")],
										[
											...Array.from({ length: startWeekday }, () =>
												h.div([h.Class("w-full aspect-square")], []),
											),
											...Array.from({ length: daysInMonth }, (_, i) =>
												dayCell(i + 1),
											),
										],
									),
								],
							),
							h.div(
								[
									h.AriaHidden(true),
									h.Class(
										"flex items-center gap-2 mt-2 text-xs text-base-content/50",
									),
								],
								[
									h.span([], [`${i18next.t("timeTravel.activity")}:`]),
									h.span([h.Class("w-3 h-3 rounded bg-primary/20")], []),
									h.span([h.Class("w-3 h-3 rounded bg-primary/50")], []),
									h.span([h.Class("w-3 h-3 rounded bg-primary")], []),
								],
							),
							h.div(
								[h.Class("mt-3 border-t border-base-300 pt-2")],
								[
									h.input([
										h.Type("date"),
										h.Class("input input-bordered input-sm w-full"),
										h.Max(todayStr),
										h.AriaLabel(i18next.t("timeTravel.date")),
										h.Value(Option.getOrElse(selected, () => "")),
										h.OnChange((date) =>
											date === ""
												? Message.ClickedExitTimeTravel()
												: Message.SelectedTimeTravelDate({ date }),
										),
									]),
								],
							),
						],
					),
				],
			),
			isActive
				? h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-xs btn-square text-secondary"),
							h.AriaLabel(i18next.t("timeTravel.exitTimeTravel")),
							h.OnClick(Message.ClickedExitTimeTravel()),
						],
						[X(h, { size: 14 })],
					)
				: h.empty,
		],
	);
};

/** v1's `TimeTravelBanner`: one translated sentence (the date's position is the locale's call) at body size, with a clear way back. */
export const timeTravelBanner = (model: Model, h: HtmlBuilder<Message>): Html =>
	Option.match(asOfOf(model.route), {
		onNone: () => h.empty,
		onSome: (asOf) =>
			h.div(
				[
					h.Role("status"),
					h.AriaLive("polite"),
					h.DataAttribute("testid", "time-travel-banner"),
					h.Class(
						"flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-secondary/30 bg-secondary/10 px-4 py-2 text-sm text-base-content lg:px-6",
					),
				],
				[
					h.span(
						[h.Class("text-secondary"), h.AriaHidden(true)],
						[Clock(h, { size: 16 })],
					),
					h.span(
						[h.Class("min-w-0 flex-1")],
						[
							i18next.t("timeTravel.viewingAsOfDate", {
								date: formatPickedDate(asOf.slice(0, 10)),
							}),
						],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm btn-outline btn-secondary"),
							h.AriaLabel(i18next.t("timeTravel.exitTimeTravel")),
							h.OnClick(Message.ClickedExitTimeTravel()),
						],
						[X(h, { size: 14 }), i18next.t("timeTravel.backToNow")],
					),
				],
			),
	});
